import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';
import '../../pages/Survey/AddSurveyForm.css';
import { Box, Typography, CircularProgress, Dialog, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

// Icon imports
const close = apiService.getBlobAssetUrl('close_popup.svg');
const addIcon = apiService.getBlobAssetUrl('Add_Icon.svg');
const delete1 = apiService.getBlobAssetUrl('Delete_section.svg');
const save = apiService.getBlobAssetUrl('confirm.svg');
const cancel = apiService.getBlobAssetUrl('cancel.svg');
const downarrow = apiService.getBlobAssetUrl('Dropdown_3.svg');
const crossicon = apiService.getBlobAssetUrl('delete_option.svg');
const remove = apiService.getBlobAssetUrl('Delete_question.svg');
const arrowicon = apiService.getBlobAssetUrl('ForwardArrow.svg');
const edit1 = apiService.getBlobAssetUrl('small_edit.svg');

// Constants
const DEFAULT_MEASURE_NAME = 'New Measure';
const MAX_WEIGHTAGE = 5;
const MIN_WEIGHTAGE = 1;

// Helper function to update a measure field in newSectionData
const updateMeasureField = (prevState, measureIndex, field, value) => {
  const updatedMeasures = [...(prevState.measures || [])];
  updatedMeasures[measureIndex] = { ...updatedMeasures[measureIndex], [field]: value };
  return { ...prevState, measures: updatedMeasures };
};



// Helper function to handle blur validation for measure name
const handleMeasureBlurValidation = (
  e,
  measureIndex,
  sections,
  newSectionData,
  setNewSectionData,
  showErrorAlert
) => {
  const trimmedName = e.target.value.trim();
  const newMeasureName = trimmedName.toLowerCase();

  if (!trimmedName) {
    showErrorAlert('Measure name cannot be empty or just spaces.');
    return;
  }

  const allExistingMeasureNames = sections.flatMap((section) =>
    (section.measures || []).map((m) => (m.measureName || '').trim().toLowerCase())
  );
  const newSectionMeasureNames = newSectionData.measures
    .filter((_, idx) => idx !== measureIndex)
    .map((m) => (m.measureName || '').trim().toLowerCase());

  if (allExistingMeasureNames.includes(newMeasureName) || newSectionMeasureNames.includes(newMeasureName)) {
    showErrorAlert(`Measure "${trimmedName}" already exists.`);
    return;
  }

  setNewSectionData((prev) => updateMeasureField(prev, measureIndex, 'measureName', trimmedName));
};

// Helper function to handle blur validation for question name
const handleQuestionBlurValidation = (
  e,
  measureIndex,
  qIdx,
  setNewSectionData,
  showErrorAlert
) => {
  const trimmed = e.target.value.trim();

  if (!trimmed) {
    showErrorAlert('Question name cannot be empty or just spaces.');
    return;
  }

  setNewSectionData((prev) => updateQuestionField(prev, measureIndex, qIdx, 'text', trimmed));
};
// Helper function to handle checkbox change for addCommentBox
const handleCommentBoxChange = (e, measureIndex, qIdx, setNewSectionData) => {
  setNewSectionData((prev) => updateQuestionField(prev, measureIndex, qIdx, 'addCommentBox', e.target.checked));
};
// Helper function to handle question text change
const handleQuestionTextChange = (e, measureIndex, qIdx, setNewSectionData) => {
  setNewSectionData((prev) => updateQuestionField(prev, measureIndex, qIdx, 'text', e.target.value));
};

// Helper function to handle question type change
const handleQuestionTypeChange = (e, measureIndex, qIdx, setNewSectionData) => {
  setNewSectionData((prev) => updateQuestionField(prev, measureIndex, qIdx, 'questionType', e.target.value));
};

// Add these helper functions at the top level with other helpers
const updateSectionField = (prevState, sectionIndex, field, value) => {
  const updatedSections = [...prevState];
  updatedSections[sectionIndex] = { ...updatedSections[sectionIndex], [field]: value };
  return [...updatedSections];
};



const updateQuestionField = (prevState, measureIndex, qIdx, field, value) => {
  if (measureIndex !== null && Array.isArray(prevState.measures)) {
    const updatedMeasures = [...prevState.measures];
    const measure = { ...updatedMeasures[measureIndex] };
    const updatedQuestions = [...(measure.questions || [])];

    if (!updatedQuestions[qIdx]) return prevState;

    updatedQuestions[qIdx] = {
      ...updatedQuestions[qIdx],
      [field]: value,
    };

    measure.questions = updatedQuestions;
    updatedMeasures[measureIndex] = measure;

    return { ...prevState, measures: updatedMeasures };
  } else {
    // fallback to newQuestions array
    const updatedQuestions = [...(prevState.newQuestions || [])];
    if (!updatedQuestions[qIdx]) return prevState;

    updatedQuestions[qIdx] = {
      ...updatedQuestions[qIdx],
      [field]: value,
    };

    return { ...prevState, newQuestions: updatedQuestions };
  }
};


const handleSectionTitleChange = (e, sectionIndex, setSections) => {
  setSections((prev) => updateSectionField(prev, sectionIndex, 'title', e.target.value));
};

const handleSectionWeightageChange = (e, sectionIndex, setSections) => {
  setSections((prev) => updateSectionField(prev, sectionIndex, 'weightage', e.target.value));
};

const handleSectionDescriptionChange = (e, sectionIndex, setSections) => {
  setSections((prev) => updateSectionField(prev, sectionIndex, 'description', e.target.value));
};

const updateSectionMeasureField = (prevState, sectionIndex, measureIndex, field, value) => {
  const updatedSections = [...prevState];
  updatedSections[sectionIndex].measures[measureIndex] = {
    ...updatedSections[sectionIndex].measures[measureIndex],
    [field]: value,
  };
  return [...updatedSections];
};

const updateSectionQuestionField = (prevState, sectionIndex, measureIndex, qIdx, field, value) => {
  const updatedSections = [...prevState];
  updatedSections[sectionIndex].measures[measureIndex].questions[qIdx] = {
    ...updatedSections[sectionIndex].measures[measureIndex].questions[qIdx],
    [field]: value,
  };
  return [...updatedSections];
};

const handleSectionMeasureChange = (sectionIndex, measureIndex, field, value, isBlur = false, setSections, showErrorAlert) => {
  setSections((prev) => {
    const updated = [...prev];
    const measure = updated[sectionIndex].measures[measureIndex];
    if (isBlur && field === 'measureName') {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        showErrorAlert('Measure name cannot be empty or just spaces.');
        return prev;
      }
      const allExistingMeasureNames = prev.flatMap((s, sIdx) =>
        s.measures
          .filter((_, mIdx) => sIdx !== sectionIndex || mIdx !== measureIndex)
          .map((m) => (m.measureName || '').trim().toLowerCase())
      );
      if (allExistingMeasureNames.includes(trimmedValue.toLowerCase())) {
        showErrorAlert(`Measure "${trimmedValue}" already exists.`);
        return prev;
      }
      measure[field] = trimmedValue;
    } else {
      measure[field] = value;
    }
    updated[sectionIndex].measures[measureIndex] = measure;
    return updated;
  });
};

const handleSectionQuestionChange = (sectionIndex, qIdx, field, value, isBlur, measureIndex, setSections, showErrorAlert) => {
  setSections((prev) => {
    const updated = [...prev];
    const question = updated[sectionIndex].measures[measureIndex].questions[qIdx];
    if (isBlur && field === 'text') {
      const trimmed = value.trim();
      if (!trimmed) {
        showErrorAlert('Question name cannot be empty or just spaces.');
        return prev;
      }
      question[field] = trimmed;
    } else {
      question[field] = value;
    }
    updated[sectionIndex].measures[measureIndex].questions[qIdx] = question;
    return updated;
  });
};
const handleSectionAddQuestion = (sectionIndex, measureIndex, setSections) => {
  setSections((prev) => {
    const updated = [...prev];
    const newQuestion = { text: '', questionType: 'checkboxes', isNewQuestion: true, isEditing: true, addCommentBox: false };
    updated[sectionIndex].measures[measureIndex].questions = [
      ...(updated[sectionIndex].measures[measureIndex].questions || []),
      newQuestion,
    ];
    return updated;
  });
};

const handleSectionAddMeasure = (sectionIndex, uniqueId, setSections) => {
  setSections((prev) => {
    console.log('handleSectionAddMeasure triggered');
    const updated = JSON.parse(JSON.stringify(prev)); // Deep copy to ensure immutability
    const existingMeasures = updated[sectionIndex]?.measures || [];
    console.log('existing measures before add:', existingMeasures);

    if (existingMeasures.some(m => m.measureId === uniqueId)) {
      console.warn('Duplicate measure prevented');
      return prev;
    }

    const newMeasure = {
      measureId: uniqueId,
      measureName: '',
      measureDescription: '',
      isEditing: true,
      questions: [],
    };

    updated[sectionIndex].measures = [...existingMeasures, newMeasure];
    console.log('updated measures after add:', updated[sectionIndex].measures);
    return updated;
  });
};

const handleSectionSaveQuestion = (sectionIndex, qIdx, measureIndex, setSections) => {
  setSections((prev) => {
    const updated = [...prev];
    const targetMeasure = updated[sectionIndex].measures[measureIndex];
    if (!targetMeasure || !targetMeasure.questions?.[qIdx]) return prev;
    const updatedQuestions = [...targetMeasure.questions];
    updatedQuestions[qIdx] = {
      ...updatedQuestions[qIdx],
      isEditing: false,
      isNewQuestion: false,
    };
    targetMeasure.questions = updatedQuestions;
    updated[sectionIndex].measures[measureIndex] = targetMeasure;
    return updated;
  });
};

const handleSectionDeleteQuestion = (sectionIndex, qIdx, measureIndex, sections, setSections, setQuestionsToDelete) => {
  setSections((prev) => {
    const updated = [...prev];
    const question = updated[sectionIndex].measures[measureIndex].questions[qIdx];
    if (question && question.questionId && !String(question.questionId).startsWith('temp-')) {
  setQuestionsToDelete((prev) => [...prev, question.questionId]);
}
if (question) {
  updated[sectionIndex].measures[measureIndex].questions.splice(qIdx, 1);
}
    return updated;
  });
};





const QuestionBlock = ({
  question,
  qIdx,
  sectionIndex,
  measureIndex,
  isOverallFeedback,
  setSections,
  showErrorAlert,
  setQuestionsToDelete,
  sections,
  renderQuestionInput,
  hasZeroWeightage,
}) => {
  const handleSaveQuestionClick = () => {
    handleSectionSaveQuestion(sectionIndex, qIdx, measureIndex, setSections);
  };

  const handleDeleteQuestionClick = () => {
    handleSectionDeleteQuestion(sectionIndex, qIdx, measureIndex, sections, setSections, setQuestionsToDelete);
  };

  const handleEditQuestionClick = () => {
    setSections((prev) => {
      const updated = [...prev];
      updated[sectionIndex].measures[measureIndex].questions[qIdx] = {
        ...updated[sectionIndex].measures[measureIndex].questions[qIdx],
        isEditing: true,
      };
      return updated;
    });
  };

  return (
    <div className="add-survey-question-box">
      <div
        className="add-survey-question-header"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <h4>{question.isNewQuestion ? 'New Question' : 'Question'}</h4>
        {!isOverallFeedback && !hasZeroWeightage && (
          <div className="survey-form-section-actions">
            {question.isEditing ? (
              <>
                <button
                  className="survey-form-action-btn survey-form-save-button"
                  onClick={handleSaveQuestionClick}
                >
                  <img src={save} alt="Save" className="survey-form-action-icon" />
                </button>
                <button
                  className="survey-form-action-btn survey-form-close-btn"
                  onClick={handleDeleteQuestionClick}
                >
                  <img src={cancel} alt="Cancel" className="survey-form-action-icon" />
                </button>
              </>
            ) : (
              <button
                className="survey-form-action-btn survey-form-edit-btn"
                onClick={handleEditQuestionClick}
              >
                <img src={edit1} alt="Edit" className="survey-form-action-icon" />
              </button>
            )}
          </div>
        )}
        {hasZeroWeightage && (
          <div className="survey-form-section-actions">
            <button
              className="survey-form-action-btn survey-form-save-button"
              onClick={handleSaveQuestionClick}
            >
              <img src={save} alt="Save" className="survey-form-action-icon" />
            </button>
          </div>
        )}
      </div>
      {hasZeroWeightage ? (
        <div className="survey-form-form-row">
          <div className="survey-form-form-group" style={{ flex: 1 }}>
            <label>Question Name <span style={{ color: 'red' }}>*</span></label>
            <input
              type="text"
              value={question.text || ''}
              onChange={(e) =>
                handleSectionQuestionChange(
                  sectionIndex,
                  qIdx,
                  'text',
                  e.target.value,
                  false,
                  measureIndex,
                  setSections,
                  showErrorAlert
                )
              }
              onBlur={(e) =>
                handleSectionQuestionChange(
                  sectionIndex,
                  qIdx,
                  'text',
                  e.target.value,
                  true,
                  measureIndex,
                  setSections,
                  showErrorAlert
                )
              }
              placeholder="Ex: Is Resource delivered tasks on-time?"
            />
          </div>
        </div>
      ) : question.isEditing && !isOverallFeedback ? (
        <>
          <div className="survey-form-form-row">
            <div className="survey-form-form-group" style={{ flex: 1 }}>
              <label>Question Name <span style={{ color: 'red' }}>*</span></label>
              <input
                type="text"
                value={question.text || ''}
                onChange={(e) =>
                  handleSectionQuestionChange(
                    sectionIndex,
                    qIdx,
                    'text',
                    e.target.value,
                    false,
                    measureIndex,
                    setSections,
                    showErrorAlert
                  )
                }
                onBlur={(e) =>
                  handleSectionQuestionChange(
                    sectionIndex,
                    qIdx,
                    'text',
                    e.target.value,
                    true,
                    measureIndex,
                    setSections,
                    showErrorAlert
                  )
                }
                placeholder="Ex: Is Resource delivered tasks on-time?"
              />
            </div>
            {question.isNewQuestion && (
              <div className="survey-form-form-group" style={{ flex: 1 }}>
                <label>Question Type</label>
                <select
                  value={question.questionType || 'checkboxes'}
                  onChange={(e) =>
                    handleSectionQuestionChange(
                      sectionIndex,
                      qIdx,
                      'questionType',
                      e.target.value,
                      false,
                      measureIndex,
                      setSections,
                      showErrorAlert
                    )
                  }
                >
                  <option value="checkboxes">Checkboxes</option>
                  <option value="linear scale">Linear Scale</option>
                </select>
              </div>
            )}
          </div>
          {(question.questionType === 'checkboxes' || question.questionType?.toLowerCase() === 'checkboxes') && (
            <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: 'red', paddingLeft: '5px' }}>
              Note: All option weightages must be equal to 5.
            </p>
          )}
          {renderQuestionInput(question, sectionIndex, qIdx, false, measureIndex)}
          {!isOverallFeedback && (
            <div className="add-survey-form-group-cbox">
              <label>
                <input
                  type="checkbox"
                  checked={question.addCommentBox}
                  onChange={(e) =>
                    handleSectionQuestionChange(
                      sectionIndex,
                      qIdx,
                      'addCommentBox',
                      e.target.checked,
                      false,
                      measureIndex,
                      setSections,
                      showErrorAlert
                    )
                  }
                />
                Add Comment Box
              </label>
            </div>
          )}
        </>
      ) : (
        <div>
          <p>
            <strong>Question:</strong> {question.text}
          </p>
          {renderQuestionInput(question, sectionIndex, qIdx, false, measureIndex)}
        </div>
      )}
    </div>
  );
};


const EditSurveyDetails = ({ onClose, onSave, surveyId, setSnackbarMessage, setSnackbarOpen, snackbarOpen }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({
    surveyName: '',
    surveyType: '',
    startDate: '',
    endDate: '',
    description: '',
    isActive: true,
  });
  const [sections, setSections] = useState([]);
  const [sectionsToDelete, setSectionsToDelete] = useState([]);
const [measuresToDelete, setMeasuresToDelete] = useState([]);
const [questionsToDelete, setQuestionsToDelete] = useState([]);
const [optionsToDelete, setOptionsToDelete] = useState([]);

  const [errorAlertOpen, setErrorAlertOpen] = useState(false);
  const [errorAlertMessage, setErrorAlertMessage] = useState('');

  const showErrorAlert = (message) => {
    setErrorAlertMessage(message);
    setErrorAlertOpen(true);
  };

  const handleCloseErrorAlert = () => {
    setErrorAlertOpen(false);
    setErrorAlertMessage('');
  };

  const [newSectionData, setNewSectionData] = useState({
    title: '',
    description: '',
    weightage: '',
    isEditing: true,
    newMeasureName: '',
    newMeasureDescription: '',
    newQuestions: [],
    measures: [],
  });
  const [showNewSectionForm, setShowNewSectionForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [surveyTypes, setSurveyTypes] = useState([]);
  const [questionTypes, setQuestionTypes] = useState([]);
  const [isEditMode, setIsEditMode] = useState(!!surveyId);
  const [isLoading, setIsLoading] = useState(false);
  const [nameError, setNameError] = useState('');
  const [originalSurveyName, setOriginalSurveyName] = useState('');
  // ✅ Top-level helper
const mapOption = (opt) => ({
  value: opt.OptionText || '',
  isDefault: opt.IsDefault || false,
  optionId: opt.OptionID,
  weightage: opt.Weightage || ''
});

const mapOptions = (optionsArray) => {
  return Array.isArray(optionsArray) ? optionsArray.map(mapOption) : [];
};
const getQuestionType = (question, fetchedQuestionTypes) => {
  const match = fetchedQuestionTypes.find(type => type.ID === question.QuestionType);
  return match?.Type?.toLowerCase() || 'multiple choice';
};
 const mapQuestion = (question, fetchedQuestionTypes) => {
  const questionType = getQuestionType(question, fetchedQuestionTypes);

  
  const baseQuestion = {
    questionId: question.QuestionID,
    text: question.QuestionText || '',
    options: mapOptions(question.Options),

    isEditing: true, // Default to view mode
    selectedOption: null,
    questionType,
    addCommentBox: question.QuestionComments || false,
  };

  if (questionType === 'linear scale') {
    const scale = question.Scale || {};
    baseQuestion.min = String(scale.MinValue || '1');
    baseQuestion.max = String(scale.MaxValue || '5');
    baseQuestion.minLabel = scale.MinValueLabel || '';
    baseQuestion.maxLabel = scale.MaxValueLabel || '';
    baseQuestion.scaleId = scale.ScaleID || null;
  }

  if (['short answer', 'paragraph'].includes(questionType)) {
    const para = Array.isArray(question.Paragraph) && question.Paragraph.length > 0
      ? question.Paragraph[0]
      : {};
    baseQuestion.userResponse = para.ParagraphText || '';
    baseQuestion.textId = para.ParagraphID || null;
  }

  return baseQuestion;
};
const mapQuestions = (questionsArray, fetchedQuestionTypes) => {
  return Array.isArray(questionsArray)
    ? questionsArray.map(q => mapQuestion(q, fetchedQuestionTypes))
    : [];
};
 const mapMeasure = (subSection, fetchedQuestionTypes) => ({
          measureId: subSection.SubSectionID,
          measureName: subSection.SubSectionName || '',
          measureDescription: subSection.SubSectionDescription || '',
          isEditing: true,
          questions:  mapQuestions(subSection.Questions, fetchedQuestionTypes),
        });

const mapMeasures = (subSections, fetchedQuestionTypes) => {
  return Array.isArray(subSections)
    ? subSections.map(subSection => mapMeasure(subSection, fetchedQuestionTypes))
    : [];
};
const mapSection = (section, fetchedQuestionTypes) => {
          const weightage = section.SectionWeightage || '';
          const hasZeroWeightage = !weightage || weightage === '' || parseFloat(weightage) === 0;
          
          return {
            sectionId: section.SectionID,
            title: section.SectionName || 'Untitled Section',
            description: section.SectionDescription || '',
            weightage: weightage,
            hasZeroWeightage: hasZeroWeightage,
            isExpanded: true,
            isEditing: false,
            newQuestions: [],
            measures:mapMeasures(section.SubSections, fetchedQuestionTypes),
          };
        };

const mapQuestionToBackend = (q, idx) => {
    const questionTypeId = questionTypes.find(type => type.Type.toLowerCase() === q.questionType)?.ID || 1;

    const question = {
      question_name: q.text,
      question_type: questionTypeId,
      question_comments: q.addCommentBox ? 1 : 0,
    };

    if (q.questionId && !String(q.questionId).startsWith('temp-')) {
      question.question_id = q.questionId;
    }

    if (q.questionType === 'linear scale') {
      question.question_scale = {
        question_minvalue: parseInt(q.min) || 1,
        question_maxvalue: parseInt(q.max) || 5,
        question_minvaluelabel: q.minLabel || null,
        question_maxvaluelabel: q.maxLabel || null,
      };
    }

    if (['short answer', 'paragraph'].includes(q.questionType)) {
      question.question_text = {
        text: q.userResponse || '',
      };
    }

    if (['multiple choice', 'checkboxes', 'dropdown'].includes(q.questionType)) {
  
      question.question_options = (q.options || [])
  .filter(opt => opt.value && opt.value.trim() !== '') // Remove empty or blank options
  .map(opt => {
    const option = {
      option_text: opt.value.trim(),
      weightage: opt.weightage || '',
    };
    if (opt.optionId && !String(opt.optionId).startsWith('temp-')) {
      option.option_id = opt.optionId;
    }
    return option;
  });

    }


    return question;
  };
const mapSubsectionQuestions = (questions = []) => {
  return questions.map(q => mapQuestionToBackend(q));
};

const deleteOptionFromQuestion = (question, optionIndex, setOptionsToDelete) => {
  const optionId = question.options[optionIndex]?.optionId;
  if (optionId && !String(optionId).startsWith('temp-')) {
    setOptionsToDelete(prev => [
      ...prev,
      { questionId: question.questionId, optionId }
    ]);
  }
  question.options = question.options.filter((_, idx) => idx !== optionIndex);
  return question;
};

const addOptionToQuestionWrapper = (question, addOptionToQuestion) => {
  const updatedQuestion = addOptionToQuestion(question);
  return updatedQuestion === question ? null : updatedQuestion;
};

const updateOptionText = (question, optionIndex, value) => {
  question.options = [
    ...question.options.slice(0, optionIndex),
    { ...question.options[optionIndex], value },
    ...question.options.slice(optionIndex + 1),
  ];
  return question;
};

const updateWeightage = (question, optionIndex, value, showErrorAlert) => {
  const parsedValue = value === '' ? '' : parseFloat(value);
  if (parsedValue !== '' && (parsedValue < 1 || parsedValue > 5)) {
    showErrorAlert('Weightage must be between 1 and 5.');
    return null;
  }
  if (question.questionType === 'checkboxes') {
    const otherWeightage = question.options
      .filter((_, idx) => idx !== optionIndex)
      .reduce((sum, opt) => sum + (parseFloat(opt.weightage) || 0), 0);
    if (otherWeightage + (parsedValue || 0) > 5) {
      showErrorAlert('Total weightage for checkboxes cannot exceed 5.');
      return null;
    }
  }
  question.options = [
    ...question.options.slice(0, optionIndex),
    { ...question.options[optionIndex], weightage: value },
    ...question.options.slice(optionIndex + 1),
  ];
  return question;
};

const toggleCheckbox = (question, optionIndex, value) => {
  question.options = [
    ...question.options.slice(0, optionIndex),
    { ...question.options[optionIndex], isDefault: value },
    ...question.options.slice(optionIndex + 1),
  ];
  return question;
};

const selectOption = (question, value) => {
  question.selectedOption = value;
  return question;
};

const updateGenericField = (question, field, value) => {
  question[field] = value;
  return question;
};

const handleDeleteOption = (question, optionIndex, setOptionsToDelete) => {
  const optionId = question.options[optionIndex]?.optionId;
  if (optionId && !String(optionId).startsWith('temp-')) {
    setOptionsToDelete(prev => [
      ...prev,
      { questionId: question.questionId, optionId },
    ]);
  }
  question.options = question.options.filter((_, idx) => idx !== optionIndex);
  return question;
};

const updateQuestionByField = (question, field, value, optionIndex, showErrorAlert) => {
  switch (field) {
    case 'option':
      return updateOptionText(question, optionIndex, value);
    case 'weightage':
      return updateWeightage(question, optionIndex, value, showErrorAlert);
    case 'selectOption':
      return selectOption(question, value);
    case 'toggleCheckbox':
      return toggleCheckbox(question, optionIndex, value);
    default:
      return updateGenericField(question, field, value);
  }
};

const isValidSection = (sections, sectionIndex) => {
  const section = sections[sectionIndex];
  if (!section) {
    console.error(`Invalid sectionIndex: ${sectionIndex}`);
    return false;
  }
  return true;
};

const getTargetQuestions = (section, questionIndex, measureIndex) => {
  const isInsideMeasure = measureIndex !== null && measureIndex !== undefined;
  const questions = isInsideMeasure
    ? section.measures?.[measureIndex]?.questions
    : section.questions || section.newQuestions;

  if (!questions || !questions[questionIndex]) {
    console.error(`Invalid questionIndex: ${questionIndex}, measureIndex: ${measureIndex}`);
    return null;
  }

  return { questions, isInsideMeasure };
};

const updateQuestionLogic = (
  question,
  field,
  value,
  optionIndex,
  setOptionsToDelete,
  addOptionToQuestion,
  showErrorAlert
) => {
  if (field === 'deleteOption' && optionIndex !== null) {
    return handleDeleteOption(question, optionIndex, setOptionsToDelete);
  }

  if (field === 'addOption') {
    const updated = addOptionToQuestionWrapper(question, addOptionToQuestion);
    return updated ? { ...question, options: updated.options } : null;
  }

  return updateQuestionByField(question, field, value, optionIndex, showErrorAlert);
};

const updateSectionsOnQuestionChange = (
  prevSections,
  sectionIndex,
  questionIndex,
  field,
  value,
  optionIndex,
  measureIndex,
  setOptionsToDelete,
  addOptionToQuestion,
  showErrorAlert
) => {
  const updatedSections = JSON.parse(JSON.stringify(prevSections));
  const section = updatedSections[sectionIndex];

  if (!isValidSection(updatedSections, sectionIndex)) return prevSections;

  const target = getTargetQuestions(section, questionIndex, measureIndex);
  if (!target) return prevSections;

  const updatedQuestions = [...target.questions];
  const question = { ...updatedQuestions[questionIndex] };

  const updatedQuestion = updateQuestionLogic(
    question,
    field,
    value,
    optionIndex,
    setOptionsToDelete,
    addOptionToQuestion,
    showErrorAlert
  );

  if (!updatedQuestion) return prevSections;

  updatedQuestions[questionIndex] = updatedQuestion;

  if (target.isInsideMeasure) {
    const updatedMeasure = {
      ...section.measures[measureIndex],
      questions: updatedQuestions
    };
    section.measures = [...section.measures];
    section.measures[measureIndex] = updatedMeasure;
  } else {
    section.questions ? section.questions = updatedQuestions : section.newQuestions = updatedQuestions;
  }

  updatedSections[sectionIndex] = { ...section };
  return updatedSections;
};



  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        if (!surveyId) {
          setLoading(false);
          setIsEditMode(false);
          return;
        }

        const surveyTypesData = await apiService.fetchData('surveys_types');
        setSurveyTypes(Array.isArray(surveyTypesData) ? surveyTypesData : surveyTypesData.results || []);

        const questionTypesData = await apiService.fetchData('questions_types');
        const fetchedQuestionTypes = Array.isArray(questionTypesData.results) ? questionTypesData.results : [];
        setQuestionTypes(fetchedQuestionTypes);

        const data = await apiService.fetchData(`surveys/${surveyId}`);
        if (!data || !data.results || !Array.isArray(data.results) || data.results.length === 0) {
          throw new Error('Survey data is empty or malformed');
        }

        const surveyData = data.results[0];
        if (!surveyData.Sections || !Array.isArray(surveyData.Sections)) {
          surveyData.Sections = [];
        }

        setFormData({
          surveyName: surveyData.Name || '',
          surveyType: surveyData.Type || '',
          startDate: surveyData.StartDate && !isNaN(new Date(surveyData.StartDate).getTime())
            ? new Date(surveyData.StartDate).toISOString().split('T')[0]
            : '',
          endDate: surveyData.EndDate && !isNaN(new Date(surveyData.EndDate).getTime())
            ? new Date(surveyData.EndDate).toISOString().split('T')[0]
            : '',
          description: surveyData.SurveyDescription || '',
          isActive: surveyData.Active ?? true,
        });
        setOriginalSurveyName(surveyData.Name || '');

        
        
        // Helper: Convert Linear Scale
        const mapScale = (scale) => {
          if (!Array.isArray(scale) || scale.length === 0) return {
            min: '',
            max: '',
            minLabel: '',
            maxLabel: '',
            scaleId: null,
          };

          const scaleObj = scale[0];
          return {
            min: scaleObj.MinValue !== undefined ? String(scaleObj.MinValue) : '',
            max: scaleObj.MaxValue !== undefined ? String(scaleObj.MaxValue) : '',
            minLabel: scaleObj.MinValueLabel || '',
            maxLabel: scaleObj.MaxValueLabel || '',
            scaleId: scaleObj.ScaleID || null,
          };
        };

        // Helper: Convert Paragraph
        const mapParagraph = (paragraphArr) => {
          if (!Array.isArray(paragraphArr) || paragraphArr.length === 0) return {
            userResponse: '',
            textId: null,
          };

          return {
            userResponse: paragraphArr[0].ParagraphText || '',
            textId: paragraphArr[0].ParagraphID || null,
          };
        };
        const mappedSections = surveyData.Sections.map(section => mapSection(section, fetchedQuestionTypes));
        setSections(mappedSections);
        setLoading(false);
      } catch (err) {
        setError(`Failed to load survey data: ${err.message}`);
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [surveyId]);

  const handleSaveMeasure = (sectionIdx, measureIdx) => {
    const updated = [...sections];
    updated[sectionIdx].measures[measureIdx].isEditing = false;
    setSections(updated);
  };

  const handleEditMeasure = (sectionIdx, measureIdx) => {
    const updated = [...sections];
    updated[sectionIdx].measures[measureIdx].isEditing = true;
    setSections(updated);
  };

const handleMeasureChange = (sectionIdx, measureIdx, field, value, triggerValidation = false) => {
  const updated = [...sections];
  updated[sectionIdx].measures[measureIdx][field] = value;
  setSections(updated);

  if (field === 'measureName' && triggerValidation) {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      showErrorAlert('Measure name cannot be empty or just spaces.');
      return;
    }

    const newMeasureName = trimmedValue.toLowerCase();

    const allExistingMeasureNames = sections.flatMap((section, sIdx) =>
      section.measures
        .filter((_, mIdx) => sIdx !== sectionIdx || mIdx !== measureIdx)
        .map(m => (m.measureName || '').trim().toLowerCase())
    );

    if (allExistingMeasureNames.includes(newMeasureName)) {
      showErrorAlert(`Measure "${trimmedValue}" already exists.`);
    }
  }
};


  const addOptionToQuestion = useCallback((question) => {
  if (!['multiple choice', 'dropdown', 'checkboxes'].includes(question.questionType)) {
    return question;
  }
  const options = Array.isArray(question.options) ? [...question.options] : [];
  let newOptionNumber = options.length + 1;
  let newOptionValue = `Option ${newOptionNumber}`;
  while (options.some(opt => opt.value === newOptionValue)) {
    newOptionNumber++;
    newOptionValue = `Option ${newOptionNumber}`;
  }
  const newOption = {
    optionId: `temp-${Date.now()}`, // Unique ID for the option
    value: newOptionValue,
    isDefault: false,
    weightage: '',
  };
  if (options.some(opt => opt.optionId === newOption.optionId)) {
    console.log('Option already exists, skipping addition');
    return question;
  }
  console.log('Adding new option:', newOption);
  return {
    ...question,
    options: [...options, newOption],
  };
}, []);

  
const handleAddQuestion = useCallback((sectionIndex = null, measureIndex = null) => {
  const defaultQuestionType = questionTypes[0]?.Type.toLowerCase() || 'checkboxes';
  const newQuestionId = `temp-${Date.now()}`; // Generate ID first

  const newQuestion = {
    questionId: newQuestionId,
    sectionName: '',
    questionType: defaultQuestionType,
    options: ['multiple choice', 'checkboxes', 'dropdown'].includes(defaultQuestionType)
      ? [{ value: 'Option 1', isDefault: false, weightage: '', optionId: `temp-${Date.now()}` }]
      : [],
    addCommentBox: false,
    isEditing: true,
    isNewQuestion: true,
  };

  if (defaultQuestionType === 'linear scale') {
    newQuestion.min = '1';
    newQuestion.max = '5';
    newQuestion.minLabel = '';
    newQuestion.maxLabel = '';
  } else if (['short answer', 'paragraph'].includes(defaultQuestionType)) {
    newQuestion.userResponse = '';
  }

  if (sectionIndex !== null && measureIndex !== null) {
    setSections(prev => {
      console.log('Updating sections to add question');
      const updated = JSON.parse(JSON.stringify(prev)); // Deep copy
      const targetMeasure = updated?.[sectionIndex]?.measures?.[measureIndex];
      if (!targetMeasure) {
        console.error('Invalid section or measure index');
        return prev;
      }

      const alreadyExists = targetMeasure.questions?.some(q => q.questionId === newQuestionId);
      if (alreadyExists) {
        console.warn('Duplicate question prevented');
        return prev;
      }

      targetMeasure.questions = [...(targetMeasure.questions || []), newQuestion];
      console.log('Updated questions:', targetMeasure.questions);
      return updated;
    });
  } else if (sectionIndex === null && measureIndex !== null) {
    setNewSectionData(prev => {
      const updated = JSON.parse(JSON.stringify(prev)); // Deep copy
      const targetMeasure = updated.measures?.[measureIndex];
      if (!targetMeasure) {
        console.error('Invalid measure index for new section');
        return prev;
      }

      const alreadyExists = targetMeasure.questions?.some(q => q.questionId === newQuestionId);
      if (alreadyExists) {
        console.warn('Duplicate question prevented');
        return prev;
      }

      targetMeasure.questions = [...(targetMeasure.questions || []), newQuestion];
      console.log('Updated newSectionData questions:', targetMeasure.questions);
      return updated;
    });
  } else {
    console.error('Invalid indices: both sectionIndex and measureIndex are null');
    return;
  }
}, [questionTypes]);


const handleQuestionChange = useCallback(
  (sectionIndex, questionIndex, field, value, optionIndex = null, measureIndex = null) => {
    setSections(prevSections =>
      updateSectionsOnQuestionChange(
        prevSections,
        sectionIndex,
        questionIndex,
        field,
        value,
        optionIndex,
        measureIndex,
        setOptionsToDelete,
        addOptionToQuestion,
        showErrorAlert
      )
    );
  },
  [addOptionToQuestion]
);



const handleNewQuestionChange = useCallback(
  (
    index,
    field,
    value,
    optionIndex = null,
    sectionIndex = null,
    measureIndex = null
  ) => {
    const resetQuestionFields = (question, fields) => {
      const updated = { ...question };
      fields.forEach((field) => delete updated[field]);
      return updated;
    };

    const updateForQuestionType = (question, value) => {
      const qType = value.toLowerCase();
      let updated = { ...question, questionType: qType };

      if (['multiple choice', 'dropdown', 'checkboxes'].includes(qType)) {
        updated.options = question.options || [
          {
            value: 'Option 1',
            optionId: `temp-${Date.now()}`,
            isDefault: false,
            weightage: ''
          }
        ];
        updated = resetQuestionFields(updated, ['min', 'max', 'minLabel', 'maxLabel', 'userResponse']);
      } else if (qType === 'linear scale') {
        updated = {
          ...resetQuestionFields(updated, ['options', 'userResponse']),
          min: '1',
          max: '5',
          minLabel: '',
          maxLabel: ''
        };
      } else {
        updated.userResponse = ['short answer', 'paragraph'].includes(qType)
          ? question.userResponse || ''
          : undefined;
        updated = resetQuestionFields(updated, ['options', 'min', 'max', 'minLabel', 'maxLabel']);
      }

      return updated;
    };

    const updateOptionText = (question, optionIndex, value) => {
      const options = [...(question.options || [])];
      options[optionIndex] = { ...options[optionIndex], value };
      return { ...question, options };
    };

    const updateOptionWeightage = (question, optionIndex, value) => {
      const parsedValue = value === '' ? '' : parseFloat(value);
      if (parsedValue !== '' && (parsedValue < 1 || parsedValue > 5)) {
        showErrorAlert('Weightage must be between 1 and 5.');
        return question;
      }

      if (question.questionType === 'checkboxes') {
        const otherOptionsWeightage = question.options
          .filter((_, idx) => idx !== optionIndex)
          .reduce((sum, opt) => sum + (parseFloat(opt.weightage) || 0), 0);

        if (otherOptionsWeightage + (parsedValue || 0) > 5) {
          showErrorAlert('Total weightage for checkboxes cannot exceed 5.');
          return question;
        }
      }

      const options = [...(question.options || [])];
      options[optionIndex] = { ...options[optionIndex], weightage: value };
      return { ...question, options };
    };

    const updateQuestion = (question) => {
      if (field === 'questionType') {
        return updateForQuestionType(question, value);
      }

      if (field === 'option' && optionIndex !== null && question.options?.[optionIndex]) {
        return updateOptionText(question, optionIndex, value);
      }

      if (field === 'weightage' && optionIndex !== null && question.options?.[optionIndex]) {
        return updateOptionWeightage(question, optionIndex, value);
      }

      if (field === 'addOption') {
        const updatedQuestion = addOptionToQuestion(question);
        console.log(
          `Adding option to new question ${index}, sectionIndex: ${sectionIndex}, measureIndex: ${measureIndex}, updated options:`,
          updatedQuestion.options
        );
        return updatedQuestion;
      }

      if (field === 'deleteOption' && optionIndex !== null) {
        if (!question.options || question.options.length <= 1) {
          showErrorAlert('At least one option is required.');
          return question;
        }
        return {
          ...question,
          options: question.options.filter((_, idx) => idx !== optionIndex)
        };
      }

      return { ...question, [field]: value };
    };

    // --- Existing Section (edit mode) ---
    if (sectionIndex !== null) {
      setSections((prevSections) => {
        const updatedSections = [...prevSections];
        const section = updatedSections[sectionIndex];

        if (!section?.newQuestions?.[index]) {
          console.error(`Invalid question index ${index} for section ${sectionIndex}`);
          return prevSections;
        }

        const updatedQuestions = [...section.newQuestions];
        updatedQuestions[index] = updateQuestion(updatedQuestions[index]);

        updatedSections[sectionIndex] = {
          ...section,
          newQuestions: updatedQuestions
        };

        return updatedSections;
      });
    }

    // --- New Section ---
    else {
      setNewSectionData((prev) => {
        const updated = { ...prev };

        if (measureIndex !== null && measureIndex !== undefined) {
          const updatedMeasures = Array.isArray(updated.measures) ? [...updated.measures] : [];

          if (!updatedMeasures[measureIndex]?.questions?.[index]) {
            console.error(`Invalid question index ${index} or measureIndex ${measureIndex} for newSectionData`);
            return prev;
          }

          const updatedQuestions = [...updatedMeasures[measureIndex].questions];
          updatedQuestions[index] = updateQuestion(updatedQuestions[index]);
          updatedMeasures[measureIndex] = {
            ...updatedMeasures[measureIndex],
            questions: updatedQuestions
          };

          return { ...updated, measures: updatedMeasures };
        } else {
          const updatedQuestions = Array.isArray(updated.newQuestions)
            ? [...updated.newQuestions]
            : [];

          if (!updatedQuestions[index]) {
            console.error(`Invalid question index ${index} for newSectionData`);
            return prev;
          }

          updatedQuestions[index] = updateQuestion(updatedQuestions[index]);

          return { ...updated, newQuestions: updatedQuestions };
        }
      });
    }
  },
  [addOptionToQuestion]
);



 

  const handleDeleteSection = (sectionIndex) => {
  const section = sections[sectionIndex];
  if (section.sectionId && !String(section.sectionId).startsWith('temp-')) {
    setSectionsToDelete(prev => [...prev, section.sectionId]);
  }
  setSections(prev => prev.filter((_, idx) => idx !== sectionIndex));
};



const handleDeleteSubsection = (sectionIndex, measureIndex) => {
  const subsection = sections[sectionIndex].measures[measureIndex];
  const id = subsection.measureId || subsection.subsection_id;
  if (id && !String(id).startsWith('temp-')) {
    setMeasuresToDelete(prev => [...prev, id]);
  }
  setSections(prev => {
    const updated = [...prev];
    updated[sectionIndex].measures.splice(measureIndex, 1);
    return updated;
  });
};


  const toggleSectionVisibility = (sectionIndex) => {
    setSections(prev => {
      const updated = [...prev];
      updated[sectionIndex].isExpanded = !updated[sectionIndex].isExpanded;
      return updated;
    });
  };

  const handleSaveSection = (index) => {
    setSections(prev => {
      const updated = [...prev];
      updated[index].isEditing = false;
      return updated;
    });
  };

  const handleEditSection = (sectionIdx) => {
    setSections(prev => {
      const updated = [...prev];
      updated[sectionIdx].isEditing = true;
      return updated;
    });
  };

const [hasMeasureError, setHasMeasureError] = useState(false); 
const mapNewQuestionForSection = (q) => ({
  questionId: q.questionId || `temp-${Date.now()}`,
  text: q.text || 'New question text',
  options: Array.isArray(q.options)
    ? q.options.map(opt => ({
        value: opt.value,
        isDefault: opt.isDefault,
        weightage: opt.weightage || '',
        optionId: opt.optionId || `temp-${Date.now()}`
      }))
    : [],
  questionType: q.questionType.toLowerCase(),
  addCommentBox: q.addCommentBox || false,
  isEditing: false,
  selectedOption: null,
  ...(q.questionType === 'linear scale' ? {
    min: q.min || '1',
    max: q.max || '5',
    minLabel: q.minLabel || '',
    maxLabel: q.maxLabel || '',
  } : {}),
  ...(['short answer', 'paragraph'].includes(q.questionType) ? {
    userResponse: q.userResponse || '',
  } : {}),
});
const mapNewMeasureForSection = (measure) => ({
  measureId: measure.measureId || `temp-${Date.now()}`,
  measureName: measure.measureName || '',
  measureDescription: measure.measureDescription || '',
  isEditing: false,
  questions: (measure.questions || []).map(mapNewQuestionForSection),
});


const handleSaveNewSection = () => {
  if (newSectionData.title.trim().toLowerCase() !== 'overall feedback') {
    const weightage = parseFloat(newSectionData.weightage);
    if (!newSectionData.weightage || isNaN(weightage) || weightage <= 0) {
      showErrorAlert('Section weightage is required and must be a positive number for non-Overall Feedback sections.');
      return;
    }
  }
  const trimmedTitle = newSectionData.title.trim();
  if (!trimmedTitle) {
    showErrorAlert('Section title is required.');
    return;
  }

  const existingTitles = sections.map(s => (s.title || s.section_name || '').trim().toLowerCase());
  if (existingTitles.includes(trimmedTitle.toLowerCase())) {
    showErrorAlert(`Section "${newSectionData.title}" already exists.`);
    return;
  }




  // Validate measure names
  const allExistingMeasureNames = sections.flatMap(section =>
    (section.measures || []).map(m => (m.measureName || '').trim().toLowerCase())
  );
  const newMeasureNames = new Set();
  for (const measure of newSectionData.measures || []) {
    const name = (measure.measureName || '').trim().toLowerCase();
    if (!name) {
      showErrorAlert('Measure name is required.');
      return;
    }
    if (allExistingMeasureNames.includes(name) || newMeasureNames.has(name)) {
      showErrorAlert(`Measure "${measure.measureName}" already exists.`);
      return;
    }
    newMeasureNames.add(name);
  }
  
  // New validation: Ensure at least one measure with at least one question
  if (!newSectionData.measures || newSectionData.measures.length === 0) {
    showErrorAlert('At least one measure is required for the new section.');
    return;
  }
  const hasQuestion = newSectionData.measures.some(measure =>
    Array.isArray(measure.questions) && measure.questions.length > 0
  );
  if (!hasQuestion) {
    showErrorAlert('At least one measure must contain at least one question.');
    return;
  }
  const newSection = {
    sectionId: `temp-${Date.now()}`,
    title: newSectionData.title,
    description: newSectionData.description,
    weightage: newSectionData.weightage || '',
    isEditing: false,
    isExpanded: true,
    newQuestions: [],
    measures: (newSectionData.measures || []).map(mapNewMeasureForSection),
  };

  setSections(prev => [...prev, newSection]);
  setNewSectionData({ title: '', description: '', weightage: '', isEditing: true, newQuestions: [], newMeasureName: '', newMeasureDescription: '', measures: [] });
  setShowNewSectionForm(false);
};
  const handleCancelNewSection = () => {
    setNewSectionData({ title: '', description: '', isEditing: true, newQuestions: [], newMeasureName: '', newMeasureDescription: '' });
    setShowNewSectionForm(false);
  };

  const handleNewSectionChange = (field, value) => {
    setNewSectionData(prev => ({ ...prev, [field]: value }));
  };

 const handleSaveQuestion = (sectionIndex, questionIndex, measureIndex) => {
  setSections(prev => {
    const updated = [...prev];
    const targetMeasure = updated[sectionIndex]?.measures?.[measureIndex];
    if (!targetMeasure || !targetMeasure.questions?.[questionIndex]) {
      console.error(`Invalid indices: sectionIndex=${sectionIndex}, measureIndex=${measureIndex}, questionIndex=${questionIndex}`);
      return prev;
    }
    const updatedQuestions = [...targetMeasure.questions];
    updatedQuestions[questionIndex] = {
      ...updatedQuestions[questionIndex],
      isEditing: false,
      isNewQuestion: false, 
    };
    targetMeasure.questions = updatedQuestions;
    updated[sectionIndex].measures[measureIndex] = targetMeasure;
    return updated;
  });
};

const handleEditQuestion = (sectionIndex, questionIndex, measureIndex) => {
  setSections(prev => {
    const updated = [...prev];
    const targetMeasure = updated[sectionIndex]?.measures?.[measureIndex];
    if (!targetMeasure) return prev;
    const updatedQuestions = [...(targetMeasure.questions || [])];
    if (!updatedQuestions[questionIndex]) return prev;
    updatedQuestions[questionIndex] = { ...updatedQuestions[questionIndex], isEditing: true };
    targetMeasure.questions = updatedQuestions;
    updated[sectionIndex].measures[measureIndex] = targetMeasure;
    return updated;
  });
};

  const handleCancelQuestion = (index, sectionIndex = null) => {
    if (sectionIndex !== null) {
      setSections(prev => {
        const updated = [...prev];
        const updatedQuestions = [...updated[sectionIndex].newQuestions];
        if (!updatedQuestions[index]) return prev;
        updatedQuestions.splice(index, 1);
        updated[sectionIndex].newQuestions = updatedQuestions;
        return updated;
      });
    } else {
      setNewSectionData(prev => {
        const updatedQuestions = [...prev.newQuestions];
        if (!updatedQuestions[index]) return prev;
        updatedQuestions.splice(index, 1);
        return { ...prev, newQuestions: updatedQuestions };
      });
    }
  };

 const renderOptions = (question, handleChange, sectionIndex, qIndex, isNewSection, measureIndex, isReadOnly = false) => {
  const supportsOptions = ['multiple choice', 'checkboxes', 'dropdown'].includes(question.questionType);
  if (!supportsOptions) return null;

  
 
const onChange = (field, value, optionIndex) =>
  isNewSection
    ? handleNewQuestionChange(qIndex, field, value, optionIndex, null, measureIndex)
    : handleQuestionChange(sectionIndex, qIndex, field, value, optionIndex, measureIndex); // ✅ Use correct field



const onDelete = (optionIndex) => {
  if (isNewSection) {
    handleNewQuestionChange(qIndex, 'deleteOption', null, optionIndex, null, measureIndex);
  } else {
    handleQuestionChange(sectionIndex, qIndex, 'deleteOption', null, optionIndex, measureIndex);
  }
};

const onAddOption = (e) => {
  e.stopPropagation();
  const eventId = Date.now();
  console.log(`Add Option clicked for question ${qIndex}, isNewSection: ${isNewSection}, sectionIndex: ${sectionIndex}, measureIndex: ${measureIndex}, eventId: ${eventId}`);
  if (isNewSection) {
    handleNewQuestionChange(qIndex, 'addOption', null, null, null, measureIndex);
  } else {
    handleQuestionChange(sectionIndex, qIndex, 'addOption', null, null, measureIndex);
  }
};
 
  return (
    <div className="survey-form-options-list" style={{ width: '100%' }}>
      {question.options?.map((option, optionIndex) => (
        <div
          key={optionIndex}
          className={
            question.questionType === 'multiple choice'
              ? 'survey-form-radio-group'
              : question.questionType === 'checkboxes'
              ? 'survey-form-checkbox-group'
              : 'survey-form-dropdown-group'
          }
          style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}
        >
          <span style={{ fontSize: '18px', marginRight: '8px' }}>
            {question.questionType === 'multiple choice' && '◯'}
            {question.questionType === 'checkboxes' && '▢'}
            {question.questionType === 'dropdown' && `${optionIndex + 1}.`}
          </span>
          <input
            type="text"
            value={option.value}
            onChange={(e) => onChange('option', e.target.value, optionIndex)}
            placeholder="Type here..."
            className="add-survey-option-input"
            style={{
              flex: 1,
              marginRight: '8px',
              width: '300px',
              padding: '4px',
            }}
          />
          <input
            type="number"
            value={option.weightage || ''}
            onChange={(e) => onChange('weightage', e.target.value, optionIndex)}
            placeholder="Weigh"
            className="add-survey-weightage-input"
            style={{
              width: '30px',
              padding: '8px',
              marginRight: '8px',
            }}
          />
          <button
            className="add-survey-delete-icon"
            onClick={() => onDelete(optionIndex)}
            disabled={question.options.length === 1}
            style={{
              background: 'none',
              border: 'none',
              cursor: question.options.length > 1 ? 'pointer' : 'not-allowed',
            }}
          >
            <img src={crossicon} alt="delete" className="add-survey-delete-icon" />
          </button>
        </div>
      ))}
      {supportsOptions ? (
        <div className="survey-form-add-option" style={{ marginTop: '8px', marginLeft: '8px', display: 'flex', gap: '10px',marginBottom:'10px' }}>
          <button
            type="button"
            onClick={onAddOption}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' ,pointerEvents: 'auto',}}
          >
            <img src={addIcon} alt="Add option" className="add-survey-add-icon" style={{ marginRight: '8px',height:'15px',width:'15px' }} />
            <span className="survey-form-add-option-text" style={{ color: '#0000FF', textDecoration: 'underline' }}>
              Add option
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );
};
const handleSaveNewQuestion = (measureIndex, questionIndex) => {
  setNewSectionData(prev => {
    const updatedMeasures = [...(prev.measures || [])];
    const targetMeasure = updatedMeasures[measureIndex];
    if (!targetMeasure || !targetMeasure.questions?.[questionIndex]) return prev;

    updatedMeasures[measureIndex] = {
      ...targetMeasure,
      questions: targetMeasure.questions.map((q, idx) =>
        idx === questionIndex ? { ...q, isEditing: false, isNewQuestion: false } : q
      ),
    };

    return { ...prev, measures: updatedMeasures };
  });
};

const handleDeleteNewQuestion = (measureIndex, questionIndex) => {
  setNewSectionData(prev => {
    const updatedMeasures = [...(prev.measures || [])];
    const targetMeasure = updatedMeasures[measureIndex];
    if (!targetMeasure || !targetMeasure.questions?.[questionIndex]) return prev;

    updatedMeasures[measureIndex] = {
      ...targetMeasure,
      questions: targetMeasure.questions.filter((_, idx) => idx !== questionIndex),
    };

    return { ...prev, measures: updatedMeasures };
  });
};

const handleSaveNewMeasure = (measureIndex) => {
  setNewSectionData(prev => {
    const updatedMeasures = [...(prev.measures || [])];
    if (!updatedMeasures[measureIndex]) return prev;
    updatedMeasures[measureIndex] = {
      ...updatedMeasures[measureIndex],
      isEditing: false,
    };
    return { ...prev, measures: updatedMeasures };
  });
};

const handleDeleteNewMeasure = (measureIndex) => {
  setNewSectionData(prev => {
    const updatedMeasures = [...(prev.measures || [])];
    if (!updatedMeasures[measureIndex]) return prev;
    updatedMeasures.splice(measureIndex, 1);
    return { ...prev, measures: updatedMeasures };
  });
};
const handleEditNewMeasure = (measureIndex) => {
  setNewSectionData(prev => {
    const updatedMeasures = [...(prev.measures || [])];
    if (!updatedMeasures[measureIndex]) return prev;
    updatedMeasures[measureIndex] = {
      ...updatedMeasures[measureIndex],
      isEditing: true,
    };
    return { ...prev, measures: updatedMeasures };
  });
};

const handleEditNewQuestion = (measureIndex, questionIndex) => {
  setNewSectionData(prev => {
    const updatedMeasures = [...(prev.measures || [])];
    const targetMeasure = updatedMeasures[measureIndex];
    if (!targetMeasure || !targetMeasure.questions?.[questionIndex]) return prev;

    updatedMeasures[measureIndex] = {
      ...targetMeasure,
      questions: targetMeasure.questions.map((q, idx) =>
        idx === questionIndex ? { ...q, isEditing: true } : q
      ),
    };

    return { ...prev, measures: updatedMeasures };
  });
};

  const renderLinearScale = (question, handleChange, sectionIndex, qIndex, isOverallFeedback, isNewSection) => {
    const onChange = (field, value) =>
      isOverallFeedback
        ? null
        : handleChange(
            isNewSection ? qIndex : sectionIndex,
            qIndex,
            field,
            value,
            null,
            isNewSection ? sectionIndex : undefined
          );

    return (
      <div className="survey-form-linear-scale-group">
        <div className="add-survey-linear-scale-labels" style={{ display: 'flex', gap: '20px' }}>
          {['minLabel', 'maxLabel'].map(labelField => (
            <div className="survey-form-form-group" key={labelField}>
              <label htmlFor={labelField}>
                {labelField === 'minLabel' ? 'Minimum Value Label' : 'Maximum Value Label'}
              </label>
              <input
                type="text"
                value={question[labelField] || ''}
                onChange={e => onChange(labelField, e.target.value)}
                placeholder={`${labelField === 'minLabel' ? 'Minimum' : 'Maximum'} value label (optional)`}
                style={{
                  border: '2px solid #013E7D',
                  borderRadius: '4px',
                  padding: '3px',
                  backgroundColor: '#F5F8FA',
                  width: '200px',
                  minHeight: '30px',
                }}
              />
            </div>
          ))}
        </div>
        <div className="add-survey-linear-scale-range">
          <label>Scale From:</label>
          <select value={isOverallFeedback ? '1' : question.min || '1'} onChange={e => onChange('min', e.target.value)} disabled={isOverallFeedback}>
            {[...Array(5)].map((_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </select>
          <span>To</span>
          <select value={isOverallFeedback ? '5' : question.max || '5'} onChange={e => onChange('max', e.target.value)} disabled={isOverallFeedback}>
            {[...Array(5)].map((_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  const renderQuestionInput = (question, sectionIndex, questionIndex, isNewSection = false, measureIndex) => {
    const handleChange = isNewSection ? handleNewQuestionChange : handleQuestionChange;
    const qIndex = questionIndex;
    const isOverallFeedback = sections[sectionIndex]?.title === 'Overall Feedback';

    const renderByType = {
      'multiple choice': () => <div className="survey-form-form-row survey-form-option-row">{renderOptions(question, handleChange, sectionIndex, qIndex, isNewSection, measureIndex)}</div>,
      'checkboxes': () => <div className="survey-form-form-row survey-form-option-row">{renderOptions(question, handleChange, sectionIndex, qIndex, isNewSection, measureIndex)}</div>,
      'dropdown': () => <div className="survey-form-form-row survey-form-option-row">{renderOptions(question, handleChange, sectionIndex, qIndex, isNewSection, measureIndex)}</div>,
      'short answer': () => (
        <div className="survey-form-form-group">
          <input
            type="text"
            value={question.userResponse || ''}
            onChange={e => handleChange(
              isNewSection ? qIndex : sectionIndex,
              qIndex,
              'userResponse',
              e.target.value,
              null,
              isNewSection ? sectionIndex : measureIndex
            )}
            placeholder="Type your answer here..."
            className="add-survey-option-input"
          />
        </div>
      ),
      'paragraph': () => (
        <div className="survey-form-form-group">
          <textarea
            value={question.userResponse || ''}
            onChange={e => handleChange(
              isNewSection ? qIndex : sectionIndex,
              qIndex,
              'userResponse',
              e.target.value,
              null,
              isNewSection ? sectionIndex : measureIndex
            )}
            placeholder="Type your answer here..."
            className="survey-form-paragraph-input"
            style={{ width: '400px' }}
          />
        </div>
      ),
      'linear scale': () => renderLinearScale(question, handleChange, sectionIndex, qIndex, isOverallFeedback, isNewSection),
    };

    return renderByType[question.questionType] ? renderByType[question.questionType]() : (
      <div style={{ color: 'red' }}>Error: Unrecognized question type "{question.questionType}".</div>
    );
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    if (name === 'surveyName') {
    const isValid = /^[a-zA-Z0-9_\-\s]*$/.test(value);
    if (!isValid) {
      setNameError('No special characters are allowed.');
    } else {
      setNameError('');
    }
  }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAddNewSection = () => {
    setShowNewSectionForm(true);
  };

  const userId = sessionStorage.getItem('user_id');





const transformToApiFormat = (formData, sections, surveyId) => {
  const surveyTypeId = surveyTypes.find(type => type.Type === formData.surveyType)?.ID || 1;

  

  const mapSectionToPayload = (section, questionTypes) => {
    const payload = {
      section_name: section.title,
      section_description: section.description,
      section_weightage: section.weightage,
    };

    if (section.sectionId && !String(section.sectionId).startsWith('temp-')) {
      payload.section_id = section.sectionId;
    }

    // Ensure measures is an array to avoid map on undefined
    payload.subsections = (section.measures || []).map(measure => {
      const subsection = {
        measure_name: measure.measureName,
        measure_description: measure.measureDescription || '',
        subsection_questions:mapSubsectionQuestions(measure.questions),
      };

      if (measure.measureId && !String(measure.measureId).startsWith('temp-')) {
        subsection.subsection_id = measure.measureId;
      }

      return subsection;
    });

    return payload;
  };

  const surveyData = {
    last_modified_by: userId || null,
    survey_name: formData.surveyName,
    survey_type: surveyTypeId,
    survey_description: formData.description,
    survey_active: formData.isActive ? 1 : 0,
    survey_start_date: formData.startDate || null,
    survey_end_date: formData.endDate || null,
    sections: sections.map(section => mapSectionToPayload(section, questionTypes)),
  };

  if (surveyId) {
    surveyData.survey_id = surveyId;
  }

  return { survey_data: surveyData };
};
  const verifySurveyName = async (name) => {
    try {
      if (!name || name.trim() === '') return false;
      if (isEditMode && name === originalSurveyName) {
        setNameError('');
        return true;
      }
      const response = await apiService.postData('surveys/verifyname', { survey_name: name });
      if (response.status === 'exists') {
        setNameError('Survey name already exists');
        return false;
      } else {
        setNameError('');
        return true;
      }
    } catch (err) {
      return false;
    }
  };

// Utility to show error and stop loading
const showErrorAndStop = (message, showErrorAlert, setIsLoading) => {
  showErrorAlert(message);
  setIsLoading(false);
  return false;
};

// Validate survey name
const validateSurveyName = async (formData, verifySurveyName, showErrorAlert, setIsLoading) => {
  const nameFormatRegex = /^[a-zA-Z0-9_\-\s]*$/;
  if (!formData.surveyName || !formData.surveyName.trim()) {
    return showErrorAndStop('Survey name cannot be empty or just spaces.', showErrorAlert, setIsLoading);
  }
  if (!nameFormatRegex.test(formData.surveyName)) {
    return showErrorAndStop('No special characters are allowed in Survey Name.', showErrorAlert, setIsLoading);
  }
  const isNameValid = await verifySurveyName(formData.surveyName);
  if (!isNameValid) {
    setIsLoading(false);
    return false;
  }
  return true;
};

// Validate section and measure names
const validateSectionsAndMeasures = (sections, showErrorAlert, setIsLoading) => {
  const nameFormatRegex = /^[a-zA-Z0-9_\-\s]*$/;
  for (const section of sections) {
    if (!section.title || !section.title.trim()) {
      return showErrorAndStop('Section name cannot be empty or just spaces.', showErrorAlert, setIsLoading);
    }
    if (!nameFormatRegex.test(section.title)) {
      return showErrorAndStop(
        `Section "${section.title}" contains invalid characters. Only letters, numbers, underscores, hyphens, and spaces are allowed.`,
        showErrorAlert,
        setIsLoading
      );
    }
    for (const measure of section.measures || []) {
      const name = (measure.measureName || '').trim();
      if (!name) {
        return showErrorAndStop('Measure name is required and cannot be just spaces.', showErrorAlert, setIsLoading);
      }
      if (!nameFormatRegex.test(measure.measureName)) {
        return showErrorAndStop(
          `Measure "${measure.measureName}" in section "${section.title}" contains invalid characters. Only letters, numbers, underscores, hyphens, and spaces are allowed.`,
          showErrorAlert,
          setIsLoading
        );
      }
    }
  }
  return true;
};

// Validate new section data
const validateNewSection = (showNewSectionForm, newSectionData, showErrorAlert, setIsLoading) => {
  if (!showNewSectionForm || !newSectionData.title?.trim()) return true;
  const nameFormatRegex = /^[a-zA-Z0-9_\-\s]*$/;
  if (!newSectionData.title.trim()) {
    return showErrorAndStop('New section name cannot be empty or just spaces.', showErrorAlert, setIsLoading);
  }
  if (!nameFormatRegex.test(newSectionData.title)) {
    return showErrorAndStop(
      `New section "${newSectionData.title}" contains invalid characters. Only letters, numbers, underscores, hyphens, and spaces are allowed.`,
      showErrorAlert,
      setIsLoading
    );
  }
  for (const measure of newSectionData.measures || []) {
    if (!nameFormatRegex.test(measure.measureName)) {
      return showErrorAndStop(
        `Measure "${measure.measureName}" in new section "${newSectionData.title}" contains invalid characters. Only letters, numbers, underscores, hyphens, and spaces are allowed.`,
        showErrorAlert,
        setIsLoading
      );
    }
  }
  return true;
};

// Validate section and measure content
const validateSectionContent = (allSections, showErrorAlert, setIsLoading) => {
  for (const section of allSections) {
    if (!section.measures || section.measures.length === 0) {
      return showErrorAndStop(
        `Section "${section.title}" must have at least one measure.`,
        showErrorAlert,
        setIsLoading
      );
    }
    const hasQuestion = section.measures.some(measure => Array.isArray(measure.questions) && measure.questions.length > 0);
    if (!hasQuestion) {
      return showErrorAndStop(
        `Section "${section.title}" must have at least one measure with at least one question.`,
        showErrorAlert,
        setIsLoading
      );
    }
  }
  return true;
};

// Validate question names
const validateQuestionNames = (updatedSections, showErrorAlert, setIsLoading) => {
  for (const section of updatedSections) {
    for (const measure of section.measures || []) {
      for (const question of measure.questions || []) {
        const name = (question.text || '').trim();
        if (!name) {
          return showErrorAndStop('All questions must have a valid name (not just spaces).', showErrorAlert, setIsLoading);
        }
      }
    }
  }
  return true;
};

// Validate section and measure uniqueness


// Validate section names for uniqueness
// const validateSectionNames = (sections, newSectionTitle, setSnackbarMessage, setSnackbarOpen, setIsLoading) => {
//   const sectionNames = new Set();
//   for (const section of sections) {
//     const name = (section.title || section.section_name || '').trim().toLowerCase();
//     if (sectionNames.has(name)) {
//       return showErrorAndStop(
//         `Section "${section.title || section.section_name}" already exists.`,
//         setSnackbarMessage,
//         setSnackbarOpen,
//         setIsLoading
//       );
//     }
//     sectionNames.add(name);
//   }
//   if (newSectionTitle && sectionNames.has(newSectionTitle.trim().toLowerCase())) {
//     return showErrorAndStop(
//       `Section "${newSectionTitle}" already exists.`,
//       setSnackbarMessage,
//       setSnackbarOpen,
//       setIsLoading
//     );
//   }
//   return true;
// };
const validateSectionNames = (
  sections,
  newSectionTitle,
  showErrorAlert,
  setIsLoading
) => {
  const trimmedNewTitle = (newSectionTitle || '').trim().toLowerCase();

  const sectionNames = new Set();

  for (const section of sections) {
    const name = (section.title || section.section_name || '').trim().toLowerCase();

    // ✅ Ignore the section if it's exactly the same as the new one
    if (name === trimmedNewTitle) continue;

    if (sectionNames.has(name)) {
      return showErrorAndStop(
        `Section "${section.title || section.section_name}" already exists.`,
        showErrorAlert,
        setIsLoading
      );
    }

    sectionNames.add(name);
  }

  // ✅ Final check to ensure it's not duplicated *outside* that exclusion
  if (sectionNames.has(trimmedNewTitle)) {
    return showErrorAndStop(
      `Section "${newSectionTitle}" already exists.`,
      showErrorAlert,
      setIsLoading
    );
  }

  return true;
};

const validateMeasureNames = (
  sections,
  newSectionTitle,
  newMeasures,
  showErrorAlert,
  setIsLoading
) => {
  const trimmedNewTitle = typeof newSectionTitle === 'string'
    ? newSectionTitle.trim().toLowerCase()
    : '';
  const safeNewMeasures = Array.isArray(newMeasures) ? newMeasures : [];

  const allMeasureNames = new Set();

  const validateExistingMeasures = () => {
    for (const section of sections) {
      const sectionTitle = (section.title || section.section_name || '').trim().toLowerCase();
      if (sectionTitle === trimmedNewTitle) continue;

      for (const measure of section.measures || []) {
        const name = (measure.measureName || '').trim().toLowerCase();
        if (!name) return throwValidation('All measures must have a name.');
        if (allMeasureNames.has(name)) {
          return throwValidation(`Measure "${measure.measureName}" already exists in another section.`);
        }
        allMeasureNames.add(name);
      }
    }
    return true;
  };

  const validateNewMeasures = () => {
    const newMeasureNames = new Set();
    for (const measure of safeNewMeasures) {
      const name = (measure.measureName || '').trim().toLowerCase();
      if (!name) return throwValidation('Measure name is required in new section.');
      if (allMeasureNames.has(name) || newMeasureNames.has(name)) {
        return throwValidation(`Measure "${measure.measureName}" already exists.`);
      }
      newMeasureNames.add(name);
    }
    return true;
  };

  const throwValidation = (msg) =>
    showErrorAndStop(msg, showErrorAlert, setIsLoading);

  return validateExistingMeasures() && validateNewMeasures();
};



// Main validateUniqueness function
const validateUniqueness = (updatedSections, showNewSectionForm, newSectionData, showErrorAlert, setIsLoading) => {
  const newSectionTitle = showNewSectionForm && newSectionData.title?.trim() ? newSectionData.title : null;
  if (!validateSectionNames(updatedSections, newSectionTitle, showErrorAlert, setIsLoading)) {
    return false;
  }
  const newMeasures = showNewSectionForm && newSectionData.measures ? newSectionData.measures : [];
  return validateMeasureNames(updatedSections, newMeasures, showErrorAlert, setIsLoading);
};

// Validate section weightage
const validateSectionWeightage = (updatedSections, showNewSectionForm, newSectionData, showErrorAlert, setIsLoading) => {
  if (showNewSectionForm && newSectionData.title?.trim()) {
    const trimmedNewTitle = newSectionData.title.trim().toLowerCase();
    const hasZeroWeightage = !newSectionData.weightage || newSectionData.weightage === '' || parseFloat(newSectionData.weightage) === 0;
    if (trimmedNewTitle !== 'overall feedback' && !hasZeroWeightage) {
      const weight = parseFloat(newSectionData.weightage);
      if (isNaN(weight) || weight <= 0) {
        return showErrorAndStop(
          `New section "${newSectionData.title}" must have a valid weightage greater than 0.`,
          showErrorAlert,
          setIsLoading
        );
      }
    }
  }

  for (const section of updatedSections) {
    const hasZeroWeightage = !section.weightage || section.weightage === '' || parseFloat(section.weightage) === 0;
    if ((section.title || section.section_name || '').trim().toLowerCase() !== 'overall feedback' && !hasZeroWeightage) {
      const weightage = parseFloat(section.weightage);
      if (isNaN(weightage) || weightage <= 0) {
        return showErrorAndStop(
          `Section "${section.title || section.section_name}" must have a valid weightage.`,
          showErrorAlert,
          setIsLoading
        );
      }
    }
  }

  const totalWeightage = updatedSections.reduce((sum, section) => {
    const hasZeroWeightage = !section.weightage || section.weightage === '' || parseFloat(section.weightage) === 0;
    if ((section.title || section.section_name || '').trim().toLowerCase() !== 'overall feedback' && !hasZeroWeightage) {
      const weight = parseFloat(section.weightage);
      return sum + (isNaN(weight) ? 0 : weight);
    }
    return sum;
  }, 0);

  // Check if there are any sections with non-zero weightage
  const hasNonZeroWeightage = updatedSections.some(section => {
    const hasZeroWeightage = !section.weightage || section.weightage === '' || parseFloat(section.weightage) === 0;
    return (section.title || section.section_name || '').trim().toLowerCase() !== 'overall feedback' && !hasZeroWeightage;
  });

  if (hasNonZeroWeightage && Math.abs(totalWeightage - 100) > 0.01) {
    return showErrorAndStop(
      `Total weightage for sections must be exactly 100%. Currently: ${totalWeightage}%`,
      showErrorAlert,
      setIsLoading
    );
  }
  return true;
};



// Define mapNewQuestion
const mapNewQuestion = (q) => ({
  questionId: q.questionId || `temp-${Date.now()}`,
  text: q.sectionName || q.text || 'New question text',
  options: Array.isArray(q.options)
    ? q.options.map(opt => ({
        value: opt.value,
        isDefault: opt.isDefault,
        weightage: opt.weightage || '',
      }))
    : [],
  questionType: q.questionType.toLowerCase(),
  addCommentBox: q.addCommentBox || false,
  isEditing: false,
  selectedOption: null,
  ...(q.questionType === 'linear scale'
    ? {
        min: q.min || '1',
        max: q.max || '5',
        minLabel: q.minLabel || '',
        maxLabel: q.maxLabel || '',
      }
    : {}),
  ...(['short answer', 'paragraph'].includes(q.questionType)
    ? {
        userResponse: q.userResponse || '',
      }
    : {}),
});

// Define validateWeightage
const validateSingleOption = (opt, j, questionText, sectionTitle, lowerType, totalCheckboxWeight, showErrorAlert, setIsLoading) => {
  const weight = opt.weightage != null ? String(opt.weightage) : '';
  if (weight.trim() === '') {
    return showErrorAndStop(
      `Weightage is required for option ${j + 1} in question "${questionText}" in section "${sectionTitle}".`,
      showErrorAlert,
      setIsLoading
    );
  }
  const parsed = parseFloat(weight);
  if (isNaN(parsed)) {
    return showErrorAndStop(
      `Weightage for option ${j + 1} in question "${questionText}" in section "${sectionTitle}" must be a valid number.`,
      showErrorAlert,
      setIsLoading
    );
  }
  if (lowerType !== 'checkboxes' && (parsed < 0 || parsed > 5)) {
    return showErrorAndStop(
      `Weightage for option ${j + 1} in question "${questionText}" in section "${sectionTitle}" must be between 0 and 5.`,
      showErrorAlert,
      setIsLoading
    );
  }
  if (lowerType === 'checkboxes' && parsed <= 0) {
    return showErrorAndStop(
      `Checkbox option ${j + 1} in "${questionText}" in section "${sectionTitle}" must have weightage > 0.`,
      showErrorAlert,
      setIsLoading
    );
  }
  return { valid: true, parsedWeight: parsed };
};

const validateWeightage = (questions, sectionTitle, measureName, showErrorAlert, setIsLoading) => {
  for (const question of questions || []) {
    const lowerType = question.questionType?.toLowerCase();
    if (!['multiple choice', 'dropdown', 'checkboxes'].includes(lowerType)) continue;
    let totalCheckboxWeight = 0;
    for (const [j, opt] of (question.options || []).entries()) {
      const result = validateSingleOption(opt, j, question.text, sectionTitle, lowerType, totalCheckboxWeight, showErrorAlert, setIsLoading);
      if (!result.valid) return false;
      if (lowerType === 'checkboxes') totalCheckboxWeight += result.parsedWeight;
    }
    if (lowerType === 'checkboxes' && Math.abs(totalCheckboxWeight - 5) > 0.01) {
      return showErrorAndStop(
        `Checkbox weightage validation failed:

Section: "${sectionTitle}"
Measure: "${measureName}"
Question: "${question.text}"

Total weightage must equal 5.
Current total: ${totalCheckboxWeight}`,
        showErrorAlert,
        setIsLoading
      );
    }
  }
  return true;
};


// Prepare sections for submission
const prepareSections = (sections, showNewSectionForm, newSectionData, mapNewQuestion) => {
  const updatedSections = sections.map(section => ({
    ...section,
    measures: section.measures.map((measure, index) => ({
      ...measure,
      questions: index === 0 && section.newQuestions ? [...(measure.questions || []), ...(section.newQuestions.map(mapNewQuestion) || [])] : measure.questions,
    })),
    newQuestions: [],
  }));

  const allSections = [...updatedSections];
  if (showNewSectionForm && newSectionData.title?.trim()) {
    allSections.push({
      title: newSectionData.title,
      description: newSectionData.description || '',
      weightage: newSectionData.weightage || '',
      measures: (newSectionData.measures || []).map(measure => ({
        measureName: measure.measureName?.trim() || '',
        measureDescription: measure.measureDescription || '',
        questions: (measure.questions || []).map(q => mapNewQuestion(q)),
      })),
    });
  }
  return allSections;
};

// Validate question weightages across sections
const validateAllWeightages = (allSections, showNewSectionForm, newSectionData, validateWeightage, showErrorAlert, setIsLoading) => {
  for (const section of allSections) {
    for (const measure of section.measures || []) {
      if (!validateWeightage(measure.questions, section.title, measure.measureName, showErrorAlert, setIsLoading)) {
        return false;
      }
    }
  }
  if (showNewSectionForm && newSectionData.title?.trim()) {
    for (const measure of newSectionData.measures || []) {
      if (!validateWeightage(measure.questions, newSectionData.title, measure.measureName, showErrorAlert, setIsLoading)) {
        return false;
      }
    }
  }
  return true;
};

// Perform deletions
const performDeletions = async (sectionsToDelete, measuresToDelete, questionsToDelete, optionsToDelete, apiService) => {
  for (const sectionId of sectionsToDelete) {
    await apiService.deleteData('delete_section', { SectionID: sectionId });
  }
  for (const measureId of measuresToDelete) {
    await apiService.deleteData('delete_subsection', { SubSectionID: measureId });
  }
  for (const questionId of questionsToDelete) {
    await apiService.deleteData('delete_question', { QuestionID: questionId });
  }
  const uniqueOptionDeletions = Array.from(
    new Map(optionsToDelete.map(({ questionId, optionId }) => [`${questionId}-${optionId}`, { questionId, optionId }])).values()
  );
  for (const { questionId, optionId } of uniqueOptionDeletions) {
    try {
      await apiService.deleteData('delete_option', { OptionID: optionId, QuestionID: questionId });
    } catch (err) {
      if (err.message?.toLowerCase().includes('not found')) {
        console.warn(`Option ${optionId} already deleted.`);
      } else {
        console.error(`Failed to delete option ${optionId}:`, err.message);
      }
    }
  }
};

// Main handleSubmit function
const handleSubmit = async () => {
  setIsLoading(true);
  try {
    if (hasMeasureError) {
      return showErrorAndStop('Please resolve all measure name duplicates before saving.', showErrorAlert, setIsLoading);
    }

    // Validate survey name
    if (!(await validateSurveyName(formData, verifySurveyName, showErrorAlert, setIsLoading))) {
      return;
    }

    if (
  showNewSectionForm &&
  newSectionData.title?.trim() &&
  !validateSectionNames(sections, newSectionData.title, showErrorAlert, setIsLoading)
) {
  return;
}


    // Prepare sections
    const allSections = prepareSections(sections, showNewSectionForm, newSectionData, mapNewQuestion);

    // Run validations
    const validations = [
      () => validateSectionsAndMeasures(allSections, showErrorAlert, setIsLoading),
      () => validateNewSection(showNewSectionForm, newSectionData, showErrorAlert, setIsLoading),
      () => validateSectionContent(allSections, showErrorAlert, setIsLoading),
      () => validateQuestionNames(allSections, showErrorAlert, setIsLoading),
      () => validateUniqueness(allSections, showNewSectionForm, newSectionData, showErrorAlert, setIsLoading),
      () => validateSectionWeightage(allSections, showNewSectionForm, newSectionData, showErrorAlert, setIsLoading),
      () => validateAllWeightages(allSections, showNewSectionForm, newSectionData, validateWeightage, showErrorAlert, setIsLoading),
    ];

    for (const validate of validations) {
      if (!validate()) return;
    }

    // Perform deletions
    await performDeletions(sectionsToDelete, measuresToDelete, questionsToDelete, optionsToDelete, apiService);

    // Clear deletion trackers
    setSectionsToDelete([]);
    setMeasuresToDelete([]);
    setQuestionsToDelete([]);
    setOptionsToDelete([]);

    // Submit data
    const payload = transformToApiFormat(formData, allSections, surveyId);
    console.log('Payload being sent to /api/surveys:', payload);

    const accessToken = process.env.REACT_APP_ACCESS_TOKEN;
    await apiService.putData('surveys', payload, accessToken);

    setSnackbarMessage('Survey updated successfully');
    setSnackbarOpen(true);
    setNewSectionData({ title: '', description: '', weightage: '', isEditing: true, newQuestions: [], newMeasureName: '', newMeasureDescription: '', measures: [] });
    setShowNewSectionForm(false);
    onSave(payload);
    setTimeout(() => onClose(), 5000);
  } catch (err) {
    console.error('Error in handleSubmit:', err.message, err.stack);
    showErrorAlert(`Failed to update survey: ${err.message || 'Unknown error'}`);
    setError(`Failed to update survey: ${err.message || 'Unknown error'}`);
  } finally {
    setIsLoading(false);
  }
};

const handleAddMeasure = (sectionIndex = null,eventId = null) => {
  console.log('Add Measure clicked at index:', sectionIndex);
  if (sectionIndex !== null) {
    // Adding measure to an existing section
    setSections(prevSections => {
      const updatedSections = [...prevSections];

      const newMeasure = {
        measureId: `temp-${Date.now()}`,
        measureName: '',
        measureDescription: '',
        questions: [],
        isEditing: true,
      };

      // Clone the specific section and its measures immutably
      const sectionToUpdate = { ...updatedSections[sectionIndex] };
      sectionToUpdate.measures = [...(sectionToUpdate.measures || []), newMeasure];

      // Update the section in the list
      updatedSections[sectionIndex] = sectionToUpdate;

      return updatedSections;
    });
  } else {
    // Adding measure to a new (unsaved) section
    setNewSectionData(prev => {
      const newMeasure = {
        measureId: `temp-${Date.now()}`,
        measureName: '',
        measureDescription: '',
        questions: [],
        isEditing: true,
      };

      return {
        ...prev,
        measures: [...(prev.measures || []), newMeasure],
      };
    });
  }
};


  const handleClose = () => {
    if (snackbarOpen) {
      setTimeout(() => {
        onClose();
      }, 5000);
    } else {
      onClose();
    }
  };

  return (
    
      <div className="add-survey-modal" id="addsurvey">
        <div className="addsurvey1">
          <div className="add-survey-modal-header" id="edit-3">
            <h2 id="edit-4">{isEditMode ? 'Edit Survey Details' : 'Create Survey'}</h2>
            <button className="add-survey-close-btn" onClick={handleClose}>
              ×
            </button>
          </div>

          {loading && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 10,
              }}
            >
              <div className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <Typography
                sx={{
                  marginTop: '12px',
                  color: '#013E7D',
                  fontSize: '16px',
                  fontWeight: 500,
                }}
              >
                Loading survey data...
              </Typography>
            </Box>
          )}
          {error && (
            <div style={{ color: 'red', textAlign: 'center', marginTop: '20px' }}>
              Error: {error}
            </div>
          )}
          {!loading && (
            <>
              <div className="add-survey-tabs">
                <button
                  className={activeTab === 'details' ? 'add-survey-active' : ''}
                  onClick={() => setActiveTab('details')}
                  id={activeTab === 'details' ? 'edit-5' : ''}
                >
                  Survey Details
                </button>
                <button
                  className={activeTab === 'questions' ? 'add-survey-active' : ''}
                  onClick={() => setActiveTab('questions')}
                  id={activeTab === 'questions' ? 'edit-5' : ''}
                >
                  Questions
                </button>
              </div>
              <div className={`add-survey-tab-content ${activeTab === 'questions' ? 'questions-tab-active' : 'details-tab-active'}`}>
                {activeTab === 'details' && (
                  <div>
                    <div className="add-survey-form alphabetically-row">
                      <div className="add-survey-form-group">
                        <label id="edit-7">Survey Name <span style={{ color: 'red' }}>*</span></label>
                        <input type="text" name="surveyName"
                          value={formData.surveyName}
                          onBlur={async () => await verifySurveyName(formData.surveyName)}
                          style={nameError ? { border: '1px solid red' } : {}}
                          onChange={handleChange}
                          required
                        />
                        {nameError && (
                          <span style={{color: 'red',fontSize: '12px',top: '100%',left: '10px',marginTop: '2px',lineHeight: '1',
                        }}>
                        {nameError}
                          </span>
                        )}
                      </div>
                      <div className="add-survey-form-group">
                        <label id="edit-7">Survey Type <span style={{ color: 'red' }}>*</span></label>
                        <select name="surveyType" value={formData.surveyType} onChange={handleChange} required>
                          <option value="">Select Type</option>
                          {surveyTypes.map(type => (
                            <option key={type.ID} value={type.Type}>
                              {type.Type}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="add-survey-form-row">
                      <div className="add-survey-form-group">
                        <label id="edit-7">Start Date <span style={{ color: 'red' }}>*</span></label>
                        <input type="date" name="startDate" value={formData.startDate}onChange={handleChange}/>
                      </div>
                      <div className="add-survey-form-group">
                        <label id="edit-7">End Date <span style={{ color: 'red' }}>*</span></label>
                        <input type="date" name="endDate" value={formData.endDate}onChange={handleChange} min={formData.startDate || undefined}/>
                      </div>
                    </div>
                    <div className="add-survey-form-group add-surey-full-width">
                      <label id="edit-7">Survey Description</label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows="4"
                      />
                    </div>
                    <div className="add-survey-form-group-cbox add-survey-checkbox-group">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleChange}
                        id="isActiveCheckbox"
                      />
                      <label htmlFor="isActiveCheckbox">Active</label>
                    </div>
                  </div>
                )}
                {activeTab === 'questions' && (
                  <div className="add-survey-questions-tab">
                   
                    {sections.map((section, sectionIndex) => {
    const isOverallFeedback = section.title?.trim().toLowerCase() === 'overall feedback';
    const hasZeroWeightage = section.hasZeroWeightage || false;

    return (
      <div key={sectionIndex} className="add-survey-section-box">
        <div className="add-survey-section-header">
          <div onClick={() => toggleSectionVisibility(sectionIndex)}>
            <img
              src={section.isExpanded ? downarrow : arrowicon}
              alt="Toggle"
              className="arrow-icon"
            />
            <input
              type="text"
              value={section.title}
              onChange={(e) => handleSectionTitleChange(e, sectionIndex, setSections)}
              disabled={isOverallFeedback}
              placeholder="Enter Section Name (Ex:Delivery)"
              style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px' ,background:'none'}}
            />
            {!isOverallFeedback && !hasZeroWeightage && (
              <span style={{ marginLeft: '10px', fontSize: '14px', fontWeight: 'bold', color: '#013E7D' }}>
                Weightage:
                <input
                  type="number"
                  value={section.weightage}
                  onChange={(e) => handleSectionWeightageChange(e, sectionIndex, setSections)}
                  disabled={isOverallFeedback}
                  placeholder="Ex:50"
                  min="0"
                  max="100"
                  onWheel={(e) => e.target.blur()}
                  style={{
                    width: '50px',
                    marginLeft: '5px',
                    fontSize: '14px',
                    padding: '4px',
                    border: '1px solid #013E7D4D',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                  }}
                />
              </span>
            )}
            <span className="add-survey-section-count">
              Section {sectionIndex + 1} of {sections.length}
            </span>
          </div>
          {!isOverallFeedback && (
            <div className="add-survey-section-actions">
              <img
                src={delete1}
                alt="delete"
                className="add-survey-add-img"
                onClick={() => handleDeleteSection(sectionIndex)}
              />
            </div>
          )}
        </div>

        {section.isExpanded && (
          <div>
            {!isOverallFeedback && !hasZeroWeightage && (
              <textarea
                value={section.description}
                onChange={(e) => handleSectionDescriptionChange(e, sectionIndex, setSections)}
                disabled={isOverallFeedback}
                placeholder="Enter section Description (Ex:Timely Delivery Feedback)"
                rows="2"
                style={{ width: '100%', marginBottom: '10px' }}
              />
            )}
            {!hasZeroWeightage && <div style={{ borderBottom: '1px solid #00000033', margin: '10px 0' }}></div>}

            {(section.measures || []).map((measure, measureIndex) => (
              <div key={measureIndex} className="add-survey-measure-block">
                {!isOverallFeedback && !hasZeroWeightage && (
                  <div
                    className="add-survey-measure-header"
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <h4>New Measure</h4>
                    {!isOverallFeedback && (
                      <div className="survey-form-section-actions">
                        {measure.isEditing ? (
                          <>
                            <button
                              className="survey-form-action-btn survey-form-save-button"
                              onClick={() => handleSaveMeasure(sectionIndex, measureIndex)}
                            >
                              <img src={save} alt="Save" className="survey-form-action-icon" />
                            </button>
                            <button
                              className="survey-form-action-btn survey-form-close-btn"
                              onClick={() => handleDeleteSubsection(sectionIndex, measureIndex)}
                            >
                              <img src={cancel} alt="Cancel" className="survey-form-action-icon" />
                            </button>
                          </>
                        ) : (
                          <button
                            className="survey-form-action-btn survey-form-edit-btn"
                            onClick={() => handleEditMeasure(sectionIndex, measureIndex)}
                          >
                            <img src={edit1} alt="Edit" className="survey-form-action-icon" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {!hasZeroWeightage && measure.isEditing ? (
                  <>
                    <div className="add-survey-form-group">
                      <label>Measure Name <span style={{ color: 'red' }}>*</span></label>
                      <input
                        type="text"
                        value={measure.measureName}
                        onChange={(e) =>
                          handleSectionMeasureChange(sectionIndex, measureIndex, 'measureName', e.target.value, false, setSections, showErrorAlert)
                        }
                        onBlur={(e) =>
                          handleSectionMeasureChange(sectionIndex, measureIndex, 'measureName', e.target.value, true, setSections, showErrorAlert)
                        }
                        placeholder="Enter Measure Name(Ex:Schedule)"
                      />
                    </div>
                    <div className="add-survey-form-group">
                      <label>Measure Description</label>
                      <textarea
                        value={measure.measureDescription}
                        onChange={(e) =>
                          handleSectionMeasureChange(sectionIndex, measureIndex, 'measureDescription', e.target.value, false, setSections, showErrorAlert)
                        }
                        placeholder="Enter Measure Description(Ex:Schedule Compliance Evaluation)"
                      />
                    </div>
                  </>
                ) : !isOverallFeedback && !hasZeroWeightage && (
                  <>
                    <p><strong>Measure:</strong> {measure.measureName}</p>
                    <p><strong>Description:</strong> {measure.measureDescription}</p>
                  </>
                )}

                {(measure.questions || []).map((q, qIdx) => (
                  <QuestionBlock
                    key={qIdx}
                    question={q}
                    qIdx={qIdx}
                    sectionIndex={sectionIndex}
                    measureIndex={measureIndex}
                    isOverallFeedback={isOverallFeedback}
                    setSections={setSections}
                    showErrorAlert={showErrorAlert}
                    setQuestionsToDelete={setQuestionsToDelete}
                    sections={sections}
                    renderQuestionInput={renderQuestionInput}
                    hasZeroWeightage={hasZeroWeightage}
                  />
                ))}


                {!hasZeroWeightage && (
                  <div id="survey-add-question-container">
                    <img src={addIcon} alt="Add" className="add-logo" style={{ height: '15px', width: '15px' }} />
                    <button
                      type="button"
                      className="add-question-btn"
                      style={{
                        border: 'none',
                        background: 'none',
                        color: 'blue',
                        fontSize: '16px',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (measureIndex === undefined || measureIndex === null) {
                          console.error('Invalid measureIndex for section:', measureIndex);
                          return;
                        }
                        handleAddQuestion(sectionIndex, measureIndex);
                      }}
                      title="Add Question"
                    >
                      Add Question
                    </button>
                  </div>
                )}
              </div>
            ))}

            {!hasZeroWeightage && (
              <div id="survey-add-measure-container">
                <img src={addIcon} alt="Add" className="add-logo" style={{ height: '15px', width: '15px' }} />
                <button
                  type="button"
                  className="add-measure-btn"
                  style={{
                    border: 'none',
                    background: 'none',
                    color: 'blue',
                    fontSize: '16px',
                    paddingBottom: '5px',
                  }}
                  onClick={() => {
                  const uniqueId = `temp-${Date.now()}`;
                  handleSectionAddMeasure(sectionIndex, uniqueId, setSections);
                  }}
                  title="Add Measure"
                >
                  Add Measure
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  })}

                    {showNewSectionForm && (
                      <div className="section-box1">
                        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div className="section-title-group">
                            <strong id="section2">New Section</strong>
                          </div>
                          <div className="section-actions">
                            <button className="add-survey-save-btn" id="section3" onClick={handleSaveNewSection}>
                              <img src={save} className="add-survey-save-btn" alt="save" />
                            </button>
                            <button id="section3" onClick={handleCancelNewSection}>
                              <img src={cancel} className="add-survey-cancel-btn" alt="cancel" />
                            </button>
                          </div>
                        </div>
                        <div className="survey-form-form-group">
                          <label>Section Name <span style={{ color: 'red' }}>*</span></label>
                          <input
                            type="text"
                            value={newSectionData.title}
                            onChange={e => handleNewSectionChange('title', e.target.value)}
                            placeholder="Enter Section Name (Ex:Delivery)"
                          />
                        </div>
                        <div className="survey-form-form-group">
                          <label>Section Description</label>
                          <textarea
                            value={newSectionData.description}
                            onChange={e => handleNewSectionChange('description', e.target.value)}
                            placeholder="Enter section Description (Ex:Timely Delivery Feedback)"
                            rows="3"
                          />
                        </div>
                        <div className="survey-form-form-group">
                           <label>Section Weightage (%) <span style={{ color: 'red' }}>*</span></label>
                            <input
                             type="number"
                             value={newSectionData.weightage || ''}
                             onChange={e => handleNewSectionChange('weightage', e.target.value)}
                             placeholder="Enter weightage (Ex:50)"
                             min="0"
                             max="100"
                             onWheel={e => e.target.blur()}
                            />
                         </div>

                        
  {(newSectionData.measures || []).map((measure, measureIndex) => (
    <div key={`new-measure-${measureIndex}`} className="add-survey-measure-block">
      <div className="add-survey-measure-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h4>New Measure</h4>
        <div className="survey-form-section-actions">
          {measure.isEditing ? (
            <>
              <button
                className="survey-form-action-btn survey-form-save-button"
                onClick={() => handleSaveNewMeasure(measureIndex)}
              >
                <img src={save} alt="Save" className="survey-form-action-icon" />
              </button>
              <button
                className="survey-form-action-btn survey-form-close-btn"
                onClick={() => handleDeleteNewMeasure(measureIndex)}
              >
                <img src={cancel} alt="Cancel" className="survey-form-action-icon" />
              </button>
            </>
          ) : (
            <button
              className="survey-form-action-btn survey-form-edit-btn"
              onClick={() => handleEditNewMeasure(measureIndex)}
            >
              <img src={edit1} alt="Edit" className="survey-form-action-icon" />
            </button>
          )}
        </div>
      </div>
      <div className="add-survey-form-group">
        <label>Measure Name <span style={{ color: 'red' }}>*</span></label>
        <input
          type="text"
          value={measure.measureName || ''}
          onChange={(e) =>
            setNewSectionData((prev) => updateMeasureField(prev, measureIndex, 'measureName', e.target.value))
          }
          onBlur={(e) =>
            handleMeasureBlurValidation(e, measureIndex, sections, newSectionData, setNewSectionData, showErrorAlert)
          }
          placeholder="Enter Measure Name(Ex:Schedule)"
        />
      </div>
      <div className="add-survey-form-group">
        <label>Measure Description</label>
        <textarea
          value={measure.measureDescription || ''}
          onChange={(e) =>
            setNewSectionData((prev) => updateMeasureField(prev, measureIndex, 'measureDescription', e.target.value))
          }
          placeholder="Enter Measure Description(Ex:Schedule Compliance Evaluation)"
        />
      </div>
      {(measure.questions || []).map((question, qIdx) => (
        <div key={`new-question-${measureIndex}-${qIdx}`} className="add-survey-question-box">
          <div className="add-survey-question-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4>{question.isNewQuestion ? 'New Question' : 'Question'}</h4>
            <div className="survey-form-section-actions">
              {question.isEditing ? (
                <>
                  <button
                    className="survey-form-action-btn survey-form-save-button"
                    onClick={() => handleSaveNewQuestion(measureIndex, qIdx)}
                  >
                    <img src={save} alt="Save" className="survey-form-action-icon" />
                  </button>
                  <button
                    className="survey-form-action-btn survey-form-close-btn"
                    onClick={() => handleDeleteNewQuestion(measureIndex, qIdx)}
                  >
                    <img src={cancel} alt="Cancel" className="survey-form-action-icon" />
                  </button>
                </>
              ) : (
                <button
                  className="survey-form-action-btn survey-form-edit-btn"
                  onClick={() => handleEditNewQuestion(measureIndex, qIdx)}
                >
                  <img src={edit1} alt="Edit" className="survey-form-action-icon" />
                </button>
              )}
            </div>
          </div>
          {question.isEditing ? (
            <>
              <div className="survey-form-form-row">
                <div className="survey-form-form-group" style={{ flex: 1 }}>
                  <label>Question Name <span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="text"
                    value={question.text || ''}
                    onChange={(e) => handleQuestionTextChange(e, measureIndex, qIdx, setNewSectionData)}
                    onBlur={(e) =>
                      handleQuestionBlurValidation(e, measureIndex, qIdx, setNewSectionData, showErrorAlert)
                    }
                    placeholder="Ex: Is Resource delivered tasks on-time?"
                  />
                </div>
                {question.isNewQuestion && (
                  <div className="survey-form-form-group" style={{ flex: 1 }}>
                    <label>Question Type</label>
                    <select
                      value={question.questionType || 'checkboxes'}
                      onChange={(e) => handleQuestionTypeChange(e, measureIndex, qIdx, setNewSectionData)}
                    >
                      <option value="checkboxes">Checkboxes</option>
                      <option value="linear scale">Linear Scale</option>
                    </select>
                  </div>
                )}
              </div>
              {(question.questionType === 'checkboxes' || question.questionType?.toLowerCase() === 'checkboxes') && (
                <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: 'red', paddingLeft: '5px' }}>
                  Note: All option weightages must be equal to 5.
                </p>
              )}
              {renderQuestionInput(question, null, qIdx, true, measureIndex)}
              <div className="add-survey-form-group-cbox">
                <label>
                  <input
                    type="checkbox"
                    checked={question.addCommentBox || false}
                    onChange={(e) => handleCommentBoxChange(e, measureIndex, qIdx, setNewSectionData)}
                  />
                  Add Comment Box
                </label>
              </div>
            </>
          ) : (
            <div>
              <p>
                <strong>Question:</strong> {question.text || ''}
              </p>
              {renderQuestionInput(question, null, qIdx, true, measureIndex)}
            </div>
          )}
        </div>
      ))}
      <div id="survey-add-question-container">
        <img src={addIcon} alt="Add" className="add-logo" style={{ height: '15px', width: '15px' }} />
        <button
          type="button"
          className="add-question-btn"
          style={{
            border: 'none',
            background: 'none',
            color: 'blue',
            fontSize: '16px',
            paddingBottom: '5px',
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (measureIndex === undefined || measureIndex === null) {
              console.error('Invalid measureIndex for new section:', measureIndex);
              return;
            }
            handleAddQuestion(null, measureIndex);
          }}
          title="Add Question"
        >
          Add Question
        </button>
      </div>
    </div>
  ))}
                        
  
  <div id="survey-add-measure-container">
  <img src={addIcon} alt="Add" className="add-logo" style={{ height: '15px', width: '15px' }} />
  <button
    type="button"
    className="add-question-btn"
    style={{
      border: 'none',
      background: 'none',
      color: 'blue',
      fontSize: '16px',
      paddingBottom: '5px',
    }}
    onClick={(e) => {
      e.stopPropagation();
      handleAddMeasure();
    }}
    title="Add Measure"
  >
    Add Measure
  </button>
</div>
</div>
)}
              <div className="add-survey-section-footer">
              <button type="button"className="add-survey-add-section-btn" id="section5" onClick={handleAddNewSection}>
                <img src={addIcon} alt="Add" className="add-logo" /> Add Section
              </button>
              </div>
            </div>
            )}
             </div>
              <div className="add-survey-modal-footer">
                <button type="button" id="add-survey-cancel-btn" onClick={handleClose}>
                  Cancel
                </button>
                <button type="button" id="add-survey-save-btn" disabled={isLoading} onClick={handleSubmit}>
                  {isLoading ? (
                    <>
                      <CircularProgress size={20} style={{ marginRight: '8px', color: 'white' }} />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
              </div>
            </>
          )}
          <Dialog
            open={errorAlertOpen}
            onClose={handleCloseErrorAlert}
            sx={{
              zIndex: 10000,
              '& .MuiDialog-paper': {
                borderRadius: '16px',
                padding: '24px',
                textAlign: 'center',
                minWidth: '350px',
                maxWidth: '400px',
                boxShadow: '0 24px 48px rgba(0,0,0,0.2)'
              }
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <ErrorOutlineIcon style={{ fontSize: 64, color: '#D32F2F', opacity: 0.9 }} />
            </div>
            <Typography variant="h5" style={{ fontWeight: 700, color: '#1a1a1a', marginBottom: '12px', fontFamily: 'Mulish, sans-serif' }}>
              Attention Needed
            </Typography>
            <DialogContent style={{ padding: '0 12px 24px' }}>
              <DialogContentText style={{ color: '#555', fontSize: '15px', lineHeight: 1.6, fontFamily: 'Mulish, sans-serif', whiteSpace: 'pre-line' }}>
                {errorAlertMessage}
              </DialogContentText>
            </DialogContent>
            <DialogActions style={{ justifyContent: 'center', padding: 0 }}>
              <Button
                onClick={handleCloseErrorAlert}
                variant="contained"
                style={{
                  backgroundColor: '#0066D1',
                  color: 'white',
                  borderRadius: '30px',
                  padding: '10px 40px',
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: 600,
                  boxShadow: '0 8px 16px rgba(0, 102, 209, 0.24)',
                  minWidth: '120px'
                }}
              >
                Okay, got it
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </div>
    
  );
};

export default EditSurveyDetails;
